import { parseBlocksAppFields } from './utils';
import { reduce, map, remove, compact, get, set } from 'lodash';
export const manifestInMediaBaseUrl = 'https://wixmp-6f01a4ce0453f15410e74e05.wixmp.com';
const isWidgetComponent = (comp) => {
    return comp.type === 'STUDIO_WIDGET';
};
const getUniqueControllerType = (appDefinitionId, widgetPageId) => `${appDefinitionId}-${widgetPageId}`;
const translateManifest = (manifest, translationPaths, translateFunc) => {
    translationPaths.forEach((path) => {
        const key = get(manifest, path);
        if (typeof key === 'string') {
            set(manifest, path, translateFunc(key));
        }
    });
    return manifest;
};
const getAppRevision = async (appDefinitionId, appFields, editorSdk) => {
    if (editorSdk) {
        const revision = await getRevisionOverride(editorSdk, appDefinitionId);
        if (revision !== undefined) {
            return revision;
        }
    }
    const { revision } = parseBlocksAppFields(appFields);
    return revision;
};
export async function fetchWidgetsStageData(appDataOrOptions, translateFunc, editorSDK) {
    if (isInitialAppData(appDataOrOptions)) {
        console.warn('Using fetchWidgetsStageData(initialAppData, translateFunc) is deprecated\nPlease initialize call with options');
        return fetchWidgetsStageDataInternal({
            initialAppData: appDataOrOptions,
            translateFunc,
            editorSDK,
        });
    }
    else {
        return fetchWidgetsStageDataInternal(appDataOrOptions);
    }
}
export function isInitialAppData(maybeFetchOptions) {
    return ('appDefinitionId' in maybeFetchOptions &&
        'appFields' in maybeFetchOptions &&
        'components' in maybeFetchOptions);
}
// assumes the only occurence in the url of /<number>/ is the revision
const overrideRevision = (url, newRevision) => url.replace(/\/\d+\//, `/${newRevision}/`);
const getRevisionOverride = async (editorSdk, appDefinitionId) => {
    const overrides = await editorSdk.document.info.getAppRevisionOverrides('TOKEN');
    const revision = overrides && overrides[appDefinitionId];
    return revision?.toString();
};
async function fetchSingleManifestFile(url, revision) {
    const manifestUrl = revision ? overrideRevision(url, revision) : url;
    return fetch(`${manifestInMediaBaseUrl}${manifestUrl}`).then((response) => response.json());
}
async function getSingleWidgetsStageDataFile(url, appDefinitionId, translateFunc = (t) => t, revision) {
    const manifestUrl = revision ? overrideRevision(url, revision) : url;
    const stageDataPerWidget = await fetchSingleManifestFile(manifestUrl, revision);
    const controllersStageData = reduce(Object.keys(stageDataPerWidget), (result, widgetId) => {
        const controllerType = getUniqueControllerType(appDefinitionId, widgetId);
        const widgetStageData = stageDataPerWidget[widgetId];
        result[controllerType] = translateManifest(widgetStageData.manifest, widgetStageData.translationPaths, translateFunc);
        return result;
    }, {});
    return {
        controllersStageData,
        success: true,
        errors: {},
    };
}
export async function fetchWidgetsStageDataInternal(options) {
    const defaultTranslatFunc = (t) => t;
    const translateFunc = options.translateFunc || defaultTranslatFunc;
    const { appDefinitionId, appFields, components } = options.initialAppData;
    const { baseUrls } = appFields.platform;
    const manifestUrl = baseUrls?.blocks_widgetManifestsUrl;
    if (manifestUrl) {
        return getSingleWidgetsStageDataFile(manifestUrl, appDefinitionId, translateFunc, options.editorSDK
            ? await getRevisionOverride(options.editorSDK, appDefinitionId)
            : undefined);
    }
    const revision = await getAppRevision(appDefinitionId, appFields, options.editorSDK);
    const stageDataPerWidgetPromises = map(components, async (component) => {
        if (isWidgetComponent(component)) {
            const widgetPageId = component.data.studioWidgetId;
            const manifestUrl = `${manifestInMediaBaseUrl}/manifests/${appDefinitionId}/${widgetPageId}/${revision}/manifest.json`;
            const response = await fetch(manifestUrl);
            if (response.ok) {
                return {
                    success: true,
                    data: await response.json(),
                    id: widgetPageId,
                };
                // 403 = no manifest in the server
            }
            else if (response.status !== 403) {
                return {
                    success: false,
                    error: response.statusText,
                    id: widgetPageId,
                };
            }
        }
    });
    const stageDataPerWidget = compact(await Promise.all(stageDataPerWidgetPromises));
    const invalidWidgets = remove(stageDataPerWidget, (item) => !item?.success);
    const controllersStageData = reduce(stageDataPerWidget, (result, widgetStageData) => {
        const controllerType = getUniqueControllerType(appDefinitionId, widgetStageData.id);
        result[controllerType] = translateManifest(widgetStageData.data.manifest, widgetStageData.data.translationPaths, translateFunc);
        return result;
    }, {});
    return {
        controllersStageData,
        success: !(invalidWidgets.length > 0),
        errors: reduce(invalidWidgets, (result, item) => {
            result[item.id] = item.error;
            return result;
        }, {}),
    };
}
//# sourceMappingURL=manifestFetcher.js.map